import React, { useState, useEffect, useRef } from "react";

const slides = [
  { id: "cover", label: "Cover" },
  { id: "problem", label: "Problem" },
  { id: "solution", label: "Solution" },
  { id: "how", label: "How It Works" },
  { id: "market", label: "Market" },
  { id: "diff", label: "Differentiators" },
  { id: "model", label: "Revenue" },
  { id: "roadmap", label: "Roadmap" },
  { id: "team", label: "The Ask" },
];

const accent = "#E8FF47";
const dark = "#0D0D0D";
const mid = "#1A1A1A";
const soft = "#2C2C2C";
const muted = "#888";
const white = "#F5F5F0";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=Manrope:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body { background: ${dark}; color: ${white}; font-family: 'Manrope', sans-serif; }

  .pitch-root {
    min-height: 100vh;
    background: ${dark};
    display: flex;
    flex-direction: column;
  }

  /* NAV */
  .nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 40px;
    border-bottom: 1px solid ${soft};
    background: rgba(13,13,13,0.92);
    backdrop-filter: blur(12px);
  }

  .nav-logo {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px;
    letter-spacing: 3px;
    color: ${accent};
  }

  .nav-dots {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .nav-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: ${soft};
    border: none;
    cursor: pointer;
    transition: all 0.25s;
    padding: 0;
  }

  .nav-dot.active {
    background: ${accent};
    transform: scale(1.3);
  }

  .nav-label {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: ${muted};
    letter-spacing: 1px;
  }

  /* SLIDES */
  .slide-container {
    margin-top: 61px;
    position: relative;
  }

  .slide {
    min-height: calc(100vh - 61px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 80px 80px;
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid ${soft};
    opacity: 0;
    transform: translateY(30px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }

  .slide.visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* COVER */
  .slide-cover {
    background: ${dark};
    align-items: flex-start;
    justify-content: flex-end;
    padding-bottom: 100px;
  }

  .cover-bg {
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 50% at 80% 30%, rgba(232,255,71,0.07) 0%, transparent 70%),
      radial-gradient(ellipse 40% 60% at 10% 80%, rgba(232,255,71,0.04) 0%, transparent 70%);
  }

  .cover-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
  }

  .cover-tag {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    letter-spacing: 3px;
    color: ${accent};
    text-transform: uppercase;
    margin-bottom: 28px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .cover-tag::before {
    content: '';
    width: 32px;
    height: 1px;
    background: ${accent};
  }

  .cover-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(72px, 11vw, 140px);
    line-height: 0.9;
    letter-spacing: 2px;
    color: ${white};
    position: relative;
    z-index: 1;
    margin-bottom: 40px;
  }

  .cover-title span {
    color: ${accent};
  }

  .cover-sub {
    font-size: 16px;
    font-weight: 300;
    color: ${muted};
    max-width: 480px;
    line-height: 1.7;
    position: relative;
    z-index: 1;
  }

  .cover-stats {
    position: absolute;
    top: 100px;
    right: 80px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    z-index: 1;
  }

  .cover-stat {
    text-align: right;
  }

  .cover-stat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 48px;
    color: ${accent};
    line-height: 1;
  }

  .cover-stat-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: ${muted};
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  /* SECTION HEADER */
  .slide-num {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: ${accent};
    letter-spacing: 3px;
    margin-bottom: 16px;
  }

  .slide-heading {
    font-family: 'DM Serif Display', serif;
    font-size: clamp(36px, 5vw, 64px);
    line-height: 1.1;
    color: ${white};
    margin-bottom: 48px;
    max-width: 700px;
  }

  .slide-heading em {
    font-style: italic;
    color: ${accent};
  }

  /* PROBLEM */
  .slide-problem { background: ${mid}; }

  .problem-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    max-width: 900px;
  }

  .problem-card {
    background: ${soft};
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 4px;
    padding: 32px;
    position: relative;
    overflow: hidden;
  }

  .problem-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 3px; height: 100%;
    background: ${accent};
  }

  .problem-card-icon {
    font-size: 28px;
    margin-bottom: 16px;
  }

  .problem-card-title {
    font-family: 'DM Serif Display', serif;
    font-size: 20px;
    color: ${white};
    margin-bottom: 10px;
  }

  .problem-card-body {
    font-size: 14px;
    color: ${muted};
    line-height: 1.7;
    font-weight: 300;
  }

  /* SOLUTION */
  .slide-solution { background: ${dark}; }

  .solution-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    align-items: center;
    max-width: 1000px;
  }

  .solution-body {
    font-size: 15px;
    color: rgba(245,245,240,0.75);
    line-height: 1.8;
    font-weight: 300;
    margin-bottom: 32px;
  }

  .solution-callout {
    background: ${accent};
    color: ${dark};
    padding: 20px 28px;
    border-radius: 4px;
    font-family: 'DM Serif Display', serif;
    font-size: 18px;
    line-height: 1.4;
    font-style: italic;
  }

  .solution-visual {
    background: ${soft};
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 4px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .flow-step {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .flow-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 32px;
    color: ${accent};
    line-height: 1;
    min-width: 32px;
  }

  .flow-text {
    padding-top: 4px;
  }

  .flow-title {
    font-size: 13px;
    font-weight: 600;
    color: ${white};
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .flow-desc {
    font-size: 12px;
    color: ${muted};
    line-height: 1.5;
  }

  .flow-divider {
    width: 1px;
    height: 20px;
    background: ${soft};
    margin-left: 16px;
    border-left: 1px dashed rgba(232,255,71,0.3);
  }

  /* HOW IT WORKS */
  .slide-how { background: ${mid}; }

  .bid-schema {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    max-width: 900px;
  }

  .schema-card {
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 4px;
    padding: 24px;
    background: ${dark};
    transition: border-color 0.2s;
  }

  .schema-card:hover {
    border-color: rgba(232,255,71,0.3);
  }

  .schema-icon {
    font-size: 20px;
    margin-bottom: 12px;
  }

  .schema-title {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 2px;
    color: ${accent};
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .schema-body {
    font-size: 13px;
    color: ${muted};
    line-height: 1.6;
  }

  /* MARKET */
  .slide-market { background: ${dark}; }

  .market-numbers {
    display: flex;
    gap: 60px;
    margin-bottom: 60px;
  }

  .market-stat {
    border-left: 3px solid ${accent};
    padding-left: 24px;
  }

  .market-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 64px;
    color: ${white};
    line-height: 1;
    margin-bottom: 4px;
  }

  .market-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: ${muted};
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  .market-context {
    max-width: 640px;
    font-size: 15px;
    color: rgba(245,245,240,0.65);
    line-height: 1.8;
    font-weight: 300;
  }

  /* DIFF */
  .slide-diff { background: ${mid}; }

  .diff-table {
    width: 100%;
    max-width: 860px;
    border-collapse: collapse;
  }

  .diff-table th {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 2px;
    color: ${muted};
    text-transform: uppercase;
    text-align: left;
    padding: 12px 20px;
    border-bottom: 1px solid ${soft};
  }

  .diff-table td {
    padding: 16px 20px;
    font-size: 13px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    vertical-align: middle;
  }

  .diff-table tr:last-child td { border-bottom: none; }

  .diff-feature { color: ${white}; font-weight: 500; }

  .diff-check { color: ${accent}; font-size: 18px; }
  .diff-x { color: #555; font-size: 18px; }
  .diff-partial { color: #888; font-size: 13px; }

  .diff-table tr:hover td { background: rgba(255,255,255,0.02); }

  /* REVENUE */
  .slide-model { background: ${dark}; }

  .revenue-phases {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 760px;
  }

  .rev-phase {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 32px;
    align-items: start;
    padding: 28px 0;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .rev-phase:last-child { border-bottom: none; }

  .rev-phase-label {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 28px;
    color: ${accent};
    line-height: 1.1;
  }

  .rev-phase-sub {
    font-family: 'DM Mono', monospace;
    font-size: 9px;
    color: ${muted};
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 4px;
  }

  .rev-streams {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .rev-stream {
    background: ${soft};
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 2px;
    padding: 8px 14px;
    font-size: 12px;
    color: ${white};
    letter-spacing: 0.3px;
  }

  .rev-stream.highlight {
    background: rgba(232,255,71,0.12);
    border-color: rgba(232,255,71,0.3);
    color: ${accent};
  }

  /* ROADMAP */
  .slide-roadmap { background: ${mid}; }

  .roadmap-track {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0;
    max-width: 900px;
    position: relative;
  }

  .roadmap-track::before {
    content: '';
    position: absolute;
    top: 24px;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(90deg, ${accent}, rgba(232,255,71,0.2));
  }

  .rm-phase {
    padding: 0 24px;
    padding-top: 60px;
    position: relative;
  }

  .rm-dot {
    position: absolute;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    width: 18px; height: 18px;
    border-radius: 50%;
    background: ${dark};
    border: 2px solid ${accent};
    z-index: 1;
  }

  .rm-dot.filled { background: ${accent}; }

  .rm-phase-label {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px;
    color: ${accent};
    margin-bottom: 4px;
    letter-spacing: 1px;
  }

  .rm-phase-time {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    color: ${muted};
    letter-spacing: 2px;
    margin-bottom: 20px;
    text-transform: uppercase;
  }

  .rm-items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .rm-item {
    font-size: 13px;
    color: rgba(245,245,240,0.7);
    padding-left: 14px;
    position: relative;
    line-height: 1.5;
  }

  .rm-item::before {
    content: '—';
    position: absolute;
    left: 0;
    color: ${accent};
    font-size: 10px;
    top: 2px;
  }

  /* ASK */
  .slide-team { background: ${dark}; }

  .ask-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: start;
    max-width: 1000px;
  }

  .ask-body {
    font-size: 15px;
    color: rgba(245,245,240,0.7);
    line-height: 1.8;
    font-weight: 300;
    margin-bottom: 40px;
  }

  .ask-roles {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .ask-role {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 4px;
    background: ${mid};
    transition: border-color 0.2s, background 0.2s;
  }

  .ask-role:hover {
    border-color: rgba(232,255,71,0.25);
    background: rgba(232,255,71,0.04);
  }

  .ask-role-icon { font-size: 22px; }

  .ask-role-title {
    font-weight: 600;
    font-size: 14px;
    color: ${white};
    margin-bottom: 2px;
  }

  .ask-role-desc {
    font-size: 12px;
    color: ${muted};
  }

  .ask-cta {
    margin-top: 40px;
  }

  .cta-line {
    font-family: 'DM Serif Display', serif;
    font-size: 28px;
    font-style: italic;
    color: ${white};
    margin-bottom: 8px;
  }

  .cta-sub {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: ${accent};
    letter-spacing: 2px;
  }

  /* FOUNDER BOX */
  .founder-box {
    background: ${soft};
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 4px;
    padding: 32px;
  }

  .founder-title {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    letter-spacing: 3px;
    color: ${accent};
    text-transform: uppercase;
    margin-bottom: 20px;
  }

  .founder-point {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    font-size: 13px;
    color: rgba(245,245,240,0.75);
    line-height: 1.6;
  }

  .founder-point-bullet {
    color: ${accent};
    font-family: 'Bebas Neue', sans-serif;
    font-size: 18px;
    line-height: 1.2;
    min-width: 16px;
  }

  /* PROGRESS */
  .progress-bar {
    position: fixed;
    bottom: 0; left: 0;
    height: 2px;
    background: ${accent};
    transition: width 0.4s ease;
    z-index: 200;
  }

  @media (max-width: 768px) {
    .slide { padding: 60px 32px; }
    .problem-grid, .solution-layout, .bid-schema,
    .roadmap-track, .ask-layout, .diff-table { 
      grid-template-columns: 1fr !important;
    }
    .market-numbers { flex-direction: column; gap: 32px; }
    .cover-stats { display: none; }
    .cover-title { font-size: 64px; }
  }
`;

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop || document.body.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      const slideEls = document.querySelectorAll(".slide");
      let current = 0;
      slideEls.forEach((s, i) => {
        const rect = s.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.4) current = i;
      });
      setActiveSlide(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { progress, activeSlide };
}

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Slide({ className, children, id }) {
  const ref = useReveal();
  return (
    <section ref={ref} id={id} className={`slide ${className}`}>
      {children}
    </section>
  );
}

export default function PitchDeck() {
  const { progress, activeSlide } = useScrollProgress();

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <style>{styles}</style>
      <div className="pitch-root">

        {/* NAV */}
        <nav className="nav">
          <div className="nav-logo">BRIDGEPOINT</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span className="nav-label">{slides[activeSlide]?.label}</span>
            <div className="nav-dots">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  className={`nav-dot ${i === activeSlide ? "active" : ""}`}
                  onClick={() => scrollTo(s.id)}
                  title={s.label}
                />
              ))}
            </div>
          </div>
        </nav>

        <div className="slide-container">

          {/* COVER */}
          <Slide className="slide-cover" id="cover">
            <div className="cover-bg" />
            <div className="cover-grid" />
            <div className="cover-stats">
              <div className="cover-stat">
                <div className="cover-stat-num">$500B+</div>
                <div className="cover-stat-label">Global IT Services Market</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-num">4.2M</div>
                <div className="cover-stat-label">US Software Engineers</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-num">∅</div>
                <div className="cover-stat-label">B2B Bid Platforms for Engineering</div>
              </div>
            </div>
            <div className="cover-tag">Founding Team Pitch · 2026</div>
            <h1 className="cover-title">
              BRIDGE<span>POINT</span>
            </h1>
            <p className="cover-sub">
              The first structured bid-and-award marketplace built specifically
              for engineering firms — bringing the rigor of construction procurement
              to software project work.
            </p>
          </Slide>

          {/* PROBLEM */}
          <Slide className="slide-problem" id="problem">
            <div className="slide-num">01 — THE PROBLEM</div>
            <h2 className="slide-heading">The market has a <em>structural gap.</em></h2>
            <div className="problem-grid">
              <div className="problem-card">
                <div className="problem-card-icon">📉</div>
                <div className="problem-card-title">The Big Tech Exodus</div>
                <div className="problem-card-body">
                  Post-2022 layoffs pushed tens of thousands of experienced engineers out of large firms.
                  Many formed small practices and boutique teams — but have no structured channel to find
                  firm-to-firm project work.
                </div>
              </div>
              <div className="problem-card">
                <div className="problem-card-icon">🧩</div>
                <div className="problem-card-title">Freelance Platforms Miss the Target</div>
                <div className="problem-card-body">
                  Upwork, Toptal, and Fiverr are built for individuals. They don't serve the needs
                  of a 6-person engineering team bidding on a $200k software project. The unit of work
                  doesn't fit.
                </div>
              </div>
              <div className="problem-card">
                <div className="problem-card-icon">📋</div>
                <div className="problem-card-title">No Procurement Standard</div>
                <div className="problem-card-body">
                  Software project procurement at the SMB level is informal, inconsistent, and
                  high-risk. Clients don't know what to ask for. Vendors don't know what to promise.
                  Disputes are common, outcomes are poor.
                </div>
              </div>
              <div className="problem-card">
                <div className="problem-card-icon">🏗️</div>
                <div className="problem-card-title">Construction Solved This Decades Ago</div>
                <div className="problem-card-body">
                  The construction industry has a proven bid-and-award framework: formal scope,
                  standardized bid packages, defined deliverables, exclusions, and award discretion.
                  Software engineering has nothing equivalent.
                </div>
              </div>
            </div>
          </Slide>

          {/* SOLUTION */}
          <Slide className="slide-solution" id="solution">
            <div className="slide-num">02 — THE SOLUTION</div>
            <h2 className="slide-heading">We bring construction-grade <em>procurement rigor</em> to engineering.</h2>
            <div className="solution-layout">
              <div>
                <p className="solution-body">
                  BridgePoint is a B2B bid-and-award marketplace where client businesses post
                  scoped engineering projects and qualified teams submit structured bids — complete
                  with timelines, cost breakdowns, deliverables, inclusions, and exclusions.
                </p>
                <p className="solution-body">
                  The client retains full discretion in award selection. The winning team owns
                  project management end to end. Everyone plays by the same structured rules.
                </p>
                <div className="solution-callout">
                  "It's not hiring a freelancer. It's procuring an outcome from a professional firm."
                </div>
              </div>
              <div className="solution-visual">
                <div className="flow-step">
                  <div className="flow-num">1</div>
                  <div className="flow-text">
                    <div className="flow-title">Client Posts a Project</div>
                    <div className="flow-desc">Budget, requirements, deliverables, QA criteria, and timeline expectations — all structured fields.</div>
                  </div>
                </div>
                <div className="flow-divider" />
                <div className="flow-step">
                  <div className="flow-num">2</div>
                  <div className="flow-text">
                    <div className="flow-title">Firms Submit Bids</div>
                    <div className="flow-desc">Teams respond with standardized bid packages: cost, timeline, inclusions, exclusions, and delivery promise level.</div>
                  </div>
                </div>
                <div className="flow-divider" />
                <div className="flow-step">
                  <div className="flow-num">3</div>
                  <div className="flow-text">
                    <div className="flow-title">Client Awards the Job</div>
                    <div className="flow-desc">Full discretion. No algorithm, no "suggested match." Professional judgment, like a real procurement process.</div>
                  </div>
                </div>
                <div className="flow-divider" />
                <div className="flow-step">
                  <div className="flow-num">4</div>
                  <div className="flow-text">
                    <div className="flow-title">Team Delivers</div>
                    <div className="flow-desc">The winning firm manages the project end to end, unless PM is included in the client's scope.</div>
                  </div>
                </div>
              </div>
            </div>
          </Slide>

          {/* HOW IT WORKS */}
          <Slide className="slide-how" id="how">
            <div className="slide-num">03 — BID SCHEMA</div>
            <h2 className="slide-heading">Every bid tells the <em>whole story.</em></h2>
            <div className="bid-schema">
              {[
                { icon: "💰", title: "Cost Estimate", body: "Total project cost with breakdown by phase or milestone. Transparent, not a black box." },
                { icon: "📅", title: "Timeline", body: "Estimated start, milestones, and delivery date. With or without client-side dependencies noted." },
                { icon: "✅", title: "Inclusions", body: "Explicitly what is in scope. No ambiguity about what the client is purchasing." },
                { icon: "🚫", title: "Exclusions", body: "Explicitly what is out of scope. Protects both parties from scope creep disputes." },
                { icon: "📦", title: "Delivery Promise", body: "Full or partial product as the minimum viable commitment. Sets real expectations upfront." },
                { icon: "🔍", title: "QA Approach", body: "How quality will be verified and what acceptance criteria the team commits to meeting." },
                { icon: "🏢", title: "Team Credentials", body: "Verified portfolio, past project ratings, team composition, and relevant specializations." },
                { icon: "⚠️", title: "Risk & Assumptions", body: "Known risks and key assumptions baked into the estimate. Honesty builds trust." },
                { icon: "📋", title: "PM Scope", body: "Whether the team is self-managed or if the client will embed a PM in the process." },
              ].map((item) => (
                <div className="schema-card" key={item.title}>
                  <div className="schema-icon">{item.icon}</div>
                  <div className="schema-title">{item.title}</div>
                  <div className="schema-body">{item.body}</div>
                </div>
              ))}
            </div>
          </Slide>

          {/* MARKET */}
          <Slide className="slide-market" id="market">
            <div className="slide-num">04 — MARKET OPPORTUNITY</div>
            <h2 className="slide-heading">A massive, <em>untapped</em> market.</h2>
            <div className="market-numbers">
              <div className="market-stat">
                <div className="market-num">$500B+</div>
                <div className="market-label">Global IT services market</div>
              </div>
              <div className="market-stat">
                <div className="market-num">33M+</div>
                <div className="market-label">Small businesses in the US</div>
              </div>
              <div className="market-stat">
                <div className="market-num">↑68%</div>
                <div className="market-label">Rise in software boutique firms post-2022</div>
              </div>
            </div>
            <p className="market-context">
              Our initial target is the intersection of two growing populations: small-to-mid software
              firms that need to outsource specialized project work, and the surge of experienced engineering
              teams that formed after the great tech contraction. Neither group is served by existing platforms.
              <br /><br />
              Phase 2 expands into physical engineering — electrical, mechanical, and civil — industries
              with even more entrenched procurement needs and no modern platform serving them.
            </p>
          </Slide>

          {/* DIFFERENTIATORS */}
          <Slide className="slide-diff" id="diff">
            <div className="slide-num">05 — COMPETITIVE LANDSCAPE</div>
            <h2 className="slide-heading">Built for a category that <em>doesn't exist yet.</em></h2>
            <table className="diff-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>BridgePoint</th>
                  <th>Upwork</th>
                  <th>Toptal</th>
                  <th>Agency RFP</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Team/firm as unit of participation", "✓", "✗", "✗", "✓"],
                  ["Structured bid schema", "✓", "✗", "✗", "partial"],
                  ["Client-side award discretion", "✓", "✗", "✗", "✓"],
                  ["Exclusions & delivery promise fields", "✓", "✗", "✗", "✗"],
                  ["Bidder-owned project management", "✓", "✗", "✗", "partial"],
                  ["Scalable to physical engineering", "✓", "✗", "✗", "✗"],
                  ["Formal QA / acceptance criteria", "✓", "✗", "✗", "partial"],
                ].map(([feat, bp, uw, tt, rfp]) => (
                  <tr key={feat}>
                    <td className="diff-feature">{feat}</td>
                    <td className={bp === "✓" ? "diff-check" : bp === "✗" ? "diff-x" : ""}>{bp === "partial" ? <span className="diff-partial">Partial</span> : bp}</td>
                    <td className={uw === "✓" ? "diff-check" : uw === "✗" ? "diff-x" : ""}>{uw === "partial" ? <span className="diff-partial">Partial</span> : uw}</td>
                    <td className={tt === "✓" ? "diff-check" : tt === "✗" ? "diff-x" : ""}>{tt === "partial" ? <span className="diff-partial">Partial</span> : tt}</td>
                    <td className={rfp === "✓" ? "diff-check" : rfp === "✗" ? "diff-x" : ""}>{rfp === "partial" ? <span className="diff-partial">Partial</span> : rfp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Slide>

          {/* REVENUE */}
          <Slide className="slide-model" id="model">
            <div className="slide-num">06 — REVENUE MODEL</div>
            <h2 className="slide-heading">Multiple streams, aligned <em>incentives.</em></h2>
            <div className="revenue-phases">
              <div className="rev-phase">
                <div>
                  <div className="rev-phase-label">PHASE 1</div>
                  <div className="rev-phase-sub">Launch · Months 1–12</div>
                </div>
                <div>
                  <div className="rev-streams">
                    <div className="rev-stream highlight">Free Membership Promo</div>
                    <div className="rev-stream">Loyalty Discount Lock-in</div>
                    <div className="rev-stream">Build User Base</div>
                  </div>
                  <p style={{ marginTop: 14, fontSize: 13, color: muted, lineHeight: 1.6 }}>
                    Drive adoption with a free founding member tier. Discounted paid plans locked in
                    for early adopters — creating retention and word-of-mouth.
                  </p>
                </div>
              </div>
              <div className="rev-phase">
                <div>
                  <div className="rev-phase-label">PHASE 2</div>
                  <div className="rev-phase-sub">Monetize · Months 12–24</div>
                </div>
                <div>
                  <div className="rev-streams">
                    <div className="rev-stream highlight">Membership Tiers (Client + Bidder)</div>
                    <div className="rev-stream highlight">Transaction Fee on Awards</div>
                    <div className="rev-stream">Featured Project Listings</div>
                    <div className="rev-stream">Verified Firm Badges</div>
                  </div>
                </div>
              </div>
              <div className="rev-phase">
                <div>
                  <div className="rev-phase-label">PHASE 3</div>
                  <div className="rev-phase-sub">Scale · Year 2+</div>
                </div>
                <div>
                  <div className="rev-streams">
                    <div className="rev-stream highlight">Escrow & Payment Processing</div>
                    <div className="rev-stream">Enterprise / Agency Accounts</div>
                    <div className="rev-stream">Physical Engineering Verticals</div>
                    <div className="rev-stream">White-Label Licensing</div>
                  </div>
                </div>
              </div>
            </div>
          </Slide>

          {/* ROADMAP */}
          <Slide className="slide-roadmap" id="roadmap">
            <div className="slide-num">07 — ROADMAP</div>
            <h2 className="slide-heading">From MVP to <em>market leader.</em></h2>
            <div className="roadmap-track">
              <div className="rm-phase">
                <div className="rm-dot filled" />
                <div className="rm-phase-label">MVP</div>
                <div className="rm-phase-time">Months 1 – 6</div>
                <div className="rm-items">
                  <div className="rm-item">Core bid/award platform</div>
                  <div className="rm-item">Standardized bid schema</div>
                  <div className="rm-item">Client & bidder profiles</div>
                  <div className="rm-item">Project posting & browsing</div>
                  <div className="rm-item">Free founding membership launch</div>
                </div>
              </div>
              <div className="rm-phase">
                <div className="rm-dot filled" />
                <div className="rm-phase-label">Growth</div>
                <div className="rm-phase-time">Months 6 – 18</div>
                <div className="rm-items">
                  <div className="rm-item">Paid membership tiers</div>
                  <div className="rm-item">Transaction fee infrastructure</div>
                  <div className="rm-item">Firm verification & ratings</div>
                  <div className="rm-item">Messaging & contract tools</div>
                  <div className="rm-item">Escrow / payment processing</div>
                </div>
              </div>
              <div className="rm-phase">
                <div className="rm-dot" />
                <div className="rm-phase-label">Expand</div>
                <div className="rm-phase-time">Year 2+</div>
                <div className="rm-items">
                  <div className="rm-item">Electrical engineering vertical</div>
                  <div className="rm-item">Mechanical engineering vertical</div>
                  <div className="rm-item">Civil / infrastructure vertical</div>
                  <div className="rm-item">Enterprise & agency accounts</div>
                  <div className="rm-item">API / white-label licensing</div>
                </div>
              </div>
            </div>
          </Slide>

          {/* THE ASK */}
          <Slide className="slide-team" id="team">
            <div className="slide-num">08 — THE ASK</div>
            <h2 className="slide-heading">We're looking for <em>founding engineers.</em></h2>
            <div className="ask-layout">
              <div>
                <p className="ask-body">
                  This platform is being built by someone with a rare combination: formal CS
                  education with an AI focus, and deep real-world experience in construction and
                  manufacturing software — the exact industries whose procurement models we're
                  translating into the digital engineering world.
                </p>
                <p className="ask-body">
                  We're assembling a small, senior founding team. If you've spent time in
                  software and felt the frustration of disorganized project procurement firsthand,
                  this is the problem we're solving — and we'd like to solve it with you.
                </p>
                <div className="ask-cta">
                  <div className="cta-line">Let's build the infrastructure<br />engineering firms deserve.</div>
                </div>
              </div>
              <div>
                <div className="founder-box" style={{ marginBottom: 24 }}>
                  <div className="founder-title">Founder Background</div>
                  <div className="founder-point">
                    <div className="founder-point-bullet">↗</div>
                    Computer Science education with AI specialization
                  </div>
                  <div className="founder-point">
                    <div className="founder-point-bullet">↗</div>
                    Extensive industry experience in construction & manufacturing software systems
                  </div>
                  <div className="founder-point">
                    <div className="founder-point-bullet">↗</div>
                    Deep familiarity with real-world bid-and-award procurement processes
                  </div>
                  <div className="founder-point">
                    <div className="founder-point-bullet">↗</div>
                    Translating proven physical industry frameworks into a digital platform
                  </div>
                </div>
                <div className="ask-roles">
                  {[
                    { icon: "⚙️", title: "Full-Stack Engineer", desc: "Platform architecture, APIs, and core marketplace features" },
                    { icon: "🎨", title: "Product / UX Designer", desc: "User flows, bid schema UX, and client-facing design system" },
                    { icon: "🔐", title: "Auth & Payments Engineer", desc: "Identity, escrow infrastructure, and transaction security" },
                    { icon: "📊", title: "Growth & GTM Lead", desc: "Launch strategy, founding member acquisition, and community" },
                  ].map((r) => (
                    <div className="ask-role" key={r.title}>
                      <div className="ask-role-icon">{r.icon}</div>
                      <div>
                        <div className="ask-role-title">{r.title}</div>
                        <div className="ask-role-desc">{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Slide>

        </div>

        {/* PROGRESS BAR */}
        <div className="progress-bar" style={{ width: `${progress}%` }} />

      </div>
    </>
  );
}